import { Service } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import {
  fetchViewBackedAppJson,
  ViewBackedAppServerUnavailableError,
} from './http';
import type {
  ViewBackedAppDescriptor,
  ViewBackedAppSource,
  ViewBackedAppSourceAttachInput,
  ViewBackedAppSourceCandidate,
  ViewBackedAppSourcePreferencePatch,
} from './types';

export class ViewBackedAppApiClientService extends Service {
  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
  }

  listSources(
    descriptor: ViewBackedAppDescriptor,
    workspaceId: string
  ): Promise<unknown> {
    return this.fetchServerJson(descriptor, workspaceId, '/sources');
  }

  listSourceCandidates(
    descriptor: ViewBackedAppDescriptor,
    workspaceId: string
  ): Promise<{ candidates: ViewBackedAppSourceCandidate[] }> {
    return this.fetchServerJson(descriptor, workspaceId, '/sources/candidates');
  }

  attachSource(
    descriptor: ViewBackedAppDescriptor,
    workspaceId: string,
    input: ViewBackedAppSourceAttachInput
  ): Promise<ViewBackedAppSource> {
    return this.fetchServerJson(descriptor, workspaceId, '/sources', {
      body: JSON.stringify(input),
      method: 'POST',
    });
  }

  updateSourcePreference(
    descriptor: ViewBackedAppDescriptor,
    workspaceId: string,
    sourceId: string,
    patch: ViewBackedAppSourcePreferencePatch
  ): Promise<ViewBackedAppSource> {
    return this.fetchServerJson(
      descriptor,
      workspaceId,
      `/sources/${encodeURIComponent(sourceId)}/preference`,
      {
        body: JSON.stringify(patch),
        method: 'PATCH',
      }
    );
  }

  removeSource(
    descriptor: ViewBackedAppDescriptor,
    workspaceId: string,
    sourceId: string
  ): Promise<{ removed: boolean }> {
    return this.fetchServerJson(
      descriptor,
      workspaceId,
      `/sources/${encodeURIComponent(sourceId)}`,
      { method: 'DELETE' }
    );
  }

  private fetchServerJson<T>(
    descriptor: ViewBackedAppDescriptor,
    workspaceId: string,
    suffix: string,
    init?: RequestInit
  ): Promise<T> {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new ViewBackedAppServerUnavailableError();
    }
    return fetchViewBackedAppJson<T>(
      `/api/workspace/${encodeURIComponent(workspaceId)}/${apiSegmentForDescriptor(descriptor)}${suffix}`,
      init,
      server.fetch.bind(server) as unknown as (
        url: string,
        init?: RequestInit
      ) => Promise<Response>
    );
  }
}

function apiSegmentForDescriptor(descriptor: ViewBackedAppDescriptor): string {
  if (descriptor.apiSegment.trim()) return descriptor.apiSegment;
  throw new Error(
    `No API route registered for view-backed app "${descriptor.appKind}"`
  );
}
